import React, {
  useMemo,
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback
} from 'react';
import classNames from 'classnames';
import Transparent from '@assets/images/chroma-key.png';
import { AssetVersionItemDto, ImageMetadataDto } from '@api/Api';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { setLatestZoomOptionFromStorage } from '@context/AssetViewerStateContext';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';

import './ImageCard.less';

interface IImageCardProps {
  isExpand: boolean;
  setIsExpand(val: boolean): void;
  onReady: () => void;
  asset: AssetVersionItemDto;
  isPreparing: boolean;
  isAssetReady: boolean;
}

let IS_MOUSE_DOWN = false;
let OFFSET = [0, 0];

function ImageCard({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isPreparing,
  isAssetReady
}: IImageCardProps) {
  const fabricContext = useContext(FabricContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasContainerRef = useRef<HTMLImageElement>(null);

  const {
    shapesDrawingTools,
    isSelectedCanvasObject,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption
  } = useAssetViewerState();
  const { shape } = shapesDrawingTools;
  const isShapeDrawing = shape !== 'cursor';

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as ImageMetadataDto;
    return proxy.url || '';
  }, [asset]);

  const [imageSize, setImageSize] = useState({
    width: (asset.metadata as ImageMetadataDto).info.imageInfo?.width ?? 1280,
    height: (asset.metadata as ImageMetadataDto).info.imageInfo?.height ?? 720
  });

  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (option === 'actual-size') return 1;
      const container = containerRef?.current;
      const image = imageRef?.current;
      if (!container || !image) return 1;
      const width =
        Math.abs(imageSize.width * Math.cos(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.sin(turnoverDegree * (Math.PI / 180)));
      const height =
        Math.abs(imageSize.width * Math.sin(turnoverDegree * (Math.PI / 180))) +
        Math.abs(imageSize.height * Math.cos(turnoverDegree * (Math.PI / 180)));
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const scaleWidth = Math.floor(containerWidth / (width / 100));
      const scaleHeight = Math.floor(containerHeight / (height / 100));
      if (option === 'auto') {
        return Math.min(scaleWidth, scaleHeight) / 100;
      }
      if (option === 'fit-width') {
        return scaleWidth / 100;
      }
      if (option === 'fit-height') {
        return scaleHeight / 100;
      }
      return 1;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageSize, isExpand, turnoverDegree]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        setZoomFactor(v);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        setZoomFactor(newZoomFactor, v);
        if (!imageRef.current || !canvasContainerRef.current) return;
        imageRef.current.style.left = '50%';
        imageRef.current.style.top = '50%';
        canvasContainerRef.current.style.left = '50%';
        canvasContainerRef.current.style.top = '50%';
      }
    },
    [getZoomFactorFromOption, setZoomFactor]
  );

  useEffect(() => {
    setZoomOption(setLatestZoomOptionFromStorage());
  }, [setZoomOption]);

  useEffect(() => {
    if (!zoomOption) return;
    const newZoomFactor = getZoomFactorFromOption(zoomOption);
    setZoomFactor(newZoomFactor, zoomOption);
  }, [zoomOption, getZoomFactorFromOption, setZoomFactor]);

  useEffect(() => {
    const image = imageRef?.current;
    const container = canvasContainerRef?.current;
    if (!image || !container || !fabricContext.canvas) return;
    const rect = image.getBoundingClientRect();

    container.style.width = `${rect.width}px`;
    container.style.height = `${rect.height}px`;
    fabricContext.canvas.setWidth(rect.width);
    fabricContext.canvas.setHeight(rect.height);
    fabricContext.canvas.setViewportTransform([
      zoomFactor * 10,
      0,
      0,
      zoomFactor * 10,
      0,
      0
    ]);
    canvasContainerRef.current.style.translate = `-${Math.round(
      rect.width / 2
    )}px -${Math.round(rect.height / 2)}px`;
  }, [imageSize, zoomFactor, fabricContext.canvas, turnoverDegree]);

  const onMouseDown = (e: any) => {
    if (!imageRef.current) return;
    IS_MOUSE_DOWN = true;
    OFFSET = [
      imageRef.current.offsetLeft - (e.clientX || e.touches?.[0]?.clientX || 0),
      imageRef.current.offsetTop - (e.clientY || e.touches?.[0]?.clientY || 0)
    ];
  };
  const onMouseUp = () => {
    IS_MOUSE_DOWN = false;
  };
  const onMouseMove = (e: any) => {
    if (isShapeDrawing || isSelectedCanvasObject) return;
    if (!imageRef.current || !canvasContainerRef.current) return;
    if (!IS_MOUSE_DOWN) return;
    e.preventDefault();

    const mousePosition = {
      x: e.clientX || e.touches?.[0]?.clientX,
      y: e.clientY || e.touches?.[0]?.clientY
    };
    imageRef.current.style.left = `${mousePosition.x + OFFSET[0]}px`;
    imageRef.current.style.top = `${mousePosition.y + OFFSET[1]}px`;
    canvasContainerRef.current.style.left = `${mousePosition.x + OFFSET[0]}px`;
    canvasContainerRef.current.style.top = `${mousePosition.y + OFFSET[1]}px`;
  };
  const onMouseLeave = () => {
    IS_MOUSE_DOWN = false;
  };

  return (
    <div className="media_viewer_image" ref={containerRef}>
      <div className="media_viewer_image__container">
        <img
          ref={imageRef}
          onLoad={(e) => {
            const img = e.target as HTMLImageElement;
            setImageSize({
              width: img.naturalWidth,
              height: img.naturalHeight
            });
            onReady();
          }}
          onMouseDown={onMouseDown}
          onTouchStart={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onTouchMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onTouchEnd={onMouseLeave}
          src={fileUrl}
          alt="#"
          style={{
            ...imageSize,
            transform: `translateX(-50%) translateY(-50%)  rotate(${turnoverDegree}deg) scale(${zoomFactor})`,
            backgroundImage: `url("${Transparent}")`
          }}
        />
        {(isPreparing || !isAssetReady) && <PlaybackPreparingLoader />}

        <div
          onMouseDown={onMouseDown}
          onTouchStart={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onTouchMove={onMouseMove}
          onMouseLeave={onMouseLeave}
          onTouchEnd={onMouseLeave}
          ref={canvasContainerRef}
          className={classNames('media_viewer_image__canvas', {
            'media_viewer_image__canvas--draggable': !isShapeDrawing
          })}
          style={{
            width: '100%',
            height: '100%'
          }}
        >
          <FabricCanvas />
        </div>
        <PanelAction
          zoom={zoomFactor}
          zoomOption={zoomOption}
          setZoom={setZoom}
          isShapeDrawingToolsOpen={isShapeDrawing}
          isExpand={isExpand}
          setIsExpand={(v) => {
            if (!imageRef.current || !canvasContainerRef.current) return;
            imageRef.current.style.left = '50%';
            imageRef.current.style.top = '50%';
            canvasContainerRef.current.style.left = '50%';
            canvasContainerRef.current.style.top = '50%';
            setIsExpand(v);
          }}
          setRotate={(v) => {
            const rt = turnoverDegree + v === 360 ? 0 : turnoverDegree + v;

            setTurnoverDegree(rt);
          }}
        />
      </div>
    </div>
  );
}

export default ImageCard;
